/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Distributor } from '@wsphere/distributors/domain';
import { PermissionId, Staff } from '@wsphere/staff/domain';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { RoleAssignmentsService } from './role-assignments.service';
import { StaffService } from './staff.service';

export interface StoredPermission {
  staffId: string;
  roleId: string;
  permissionId: string;
  permission?: Staff.Security.Permission;
  role?: Staff.Security.Role;
  permissionKey: string | null;
}

export const GLOBAL_DISTRIBUTORS_STORAGE_KEY = 'globalDistributors';

const PERMISSIONS_CACHE_KEY = 'permissions';
// Bounds how long a remote role/permission change stays stale in a tab. Short enough to be unnoticeable,
// long enough that the matrix is served from cache for the vast majority of permission checks.
const PERMISSIONS_CACHE_TTL_MS = 60_000;

@Injectable({ providedIn: 'root' })
export class SecurityService {
  constructor(
    private http: HttpClient,
    private roleSvc: RoleAssignmentsService,
    private staffSvc: StaffService
  ) {}

  // Shared in-flight matrix fetch, so concurrent checks after a cache miss/expiry issue a single request.
  private permissionsFetch?: Promise<Staff.Security.StaffPermissionMatrixEntry[]>;

  /** Behaviour subject of the currently selected distributors from the global picker. Only for the admin dashboard */
  globalDistributors$ = new BehaviorSubject<Distributor.PartialModel[] | null>(null);
  /** Sets the behaviour subject for the global distributors. Only to be used by the global picker */
  setGlobalDistributors(distributors: Distributor.PartialModel[] | null) {
    this.globalDistributors$.next(distributors);
    if (distributors)
      sessionStorage.setItem(
        GLOBAL_DISTRIBUTORS_STORAGE_KEY,
        JSON.stringify(distributors.map(dist => dist.permissionKey))
      );
    else sessionStorage.removeItem(GLOBAL_DISTRIBUTORS_STORAGE_KEY);
  }

  /**
   * Roles the current staff member is allowed to grant, resolved server-side.
   * DB-authoritative when custom roles are enabled, so it reflects edited permissions.
   */
  async getAvailableRoles(permissionKey?: string | null) {
    return await firstValueFrom(
      this.http.get<Staff.Security.Role[]>(`/api/available-roles`, {
        params: permissionKey ? { permissionKey } : {},
      })
    );
  }

  async hasAccess(permissionId: PermissionId, permissionKeys: string[] | null) {
    const matrix = (await this.loadPermissionsMatrixFromStorage()).filter(
      permission => permission.permissionId === permissionId || permission.permissionId === '*'
    );

    if (permissionKeys && permissionKeys.length) {
      return matrix.some(
        permission =>
          permission.permissionKey === null ||
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          permissionKeys.some(key => key.startsWith(permission.permissionKey!))
      );
    } else return matrix.some(permission => permission.permissionKey === null);
  }

  async hasRole(roleId: string, permissionKey: string | null) {
    const matrix = (await this.loadPermissionsMatrixFromStorage()).filter(
      permission => permission.roleId === roleId || permission.roleId === '*'
    );

    return (
      matrix.filter(permission => (permissionKey ? permission.permissionKey?.startsWith(permissionKey) || false : true))
        .length > 0
    );
  }

  async loadPermissionsMatrixFromStorage(): Promise<StoredPermission[]> {
    const cached = this.readCachedMatrix();
    if (cached) return cached.map(entry => this.enrichPermission(entry));

    // Cache miss or expired: resolve server-side (DB-authoritative when custom roles are enabled). Share one
    // in-flight request so a burst of concurrent checks doesn't trigger the fetch repeatedly.
    if (!this.permissionsFetch) {
      this.permissionsFetch = this.roleSvc
        .getStaffPermissionMatrix()
        .finally(() => (this.permissionsFetch = undefined));
    }
    const matrix = await this.permissionsFetch;
    if (!matrix.length) return [];

    sessionStorage.setItem(PERMISSIONS_CACHE_KEY, JSON.stringify({ matrix, fetchedAt: Date.now() }));
    return matrix.map(entry => this.enrichPermission(entry));
  }

  /**
   * Reads the cached matrix if present and still within the TTL. The short TTL bounds how long a remote change
   * (e.g. another admin editing a role) stays stale in this tab, while still caching within that window.
   * Returns null on a miss, expiry, or an old/corrupt cache format (treated as a miss so it re-fetches).
   */
  private readCachedMatrix(): Staff.Security.StaffPermissionMatrixEntry[] | null {
    const raw = sessionStorage.getItem(PERMISSIONS_CACHE_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as { matrix?: unknown; fetchedAt?: unknown };
      // Validate the shape so corrupt/partial writes fall back to a re-fetch instead of throwing in .map().
      if (!Array.isArray(parsed?.matrix) || typeof parsed.fetchedAt !== 'number') return null;
      if (Date.now() - parsed.fetchedAt > PERMISSIONS_CACHE_TTL_MS) return null;
      return parsed.matrix as Staff.Security.StaffPermissionMatrixEntry[];
    } catch {
      return null;
    }
  }

  /** Clears the cached permission matrix so the next check re-resolves (e.g. after a role's permissions change). */
  clearPermissionsCache() {
    sessionStorage.removeItem(PERMISSIONS_CACHE_KEY);
  }

  /** Attaches the registry role/permission objects (display metadata) to a stored matrix row. */
  private enrichPermission(stored: Staff.Security.StaffPermissionMatrixEntry): StoredPermission {
    return {
      ...stored,
      permission: Staff.Security.getPermission(stored.permissionId),
      role: Staff.Security.getRole(stored.roleId),
    };
  }

  async searchDistributorsWithPermissions(
    pagination: { page?: number; limit?: number },
    search?: string,
    parentId?: string
  ) {
    return await firstValueFrom(
      this.http.get<Staff.Security.ListDistributorsWithPermissionsQueryResponse>(`/api/distributors/roles`, {
        params: {
          page: pagination.page ?? 1,
          limit: pagination.limit ?? 10,
          ...(search && { search }),
          ...(parentId && { parentId }),
        },
      })
    );
  }
  async getDistributorsWithAccess(parentId?: string, permissionKeys?: string[]) {
    return await firstValueFrom(
      this.http.get<Distributor.ListChildrenDistributorsQueryResponse>(`/api/distributors/roles/children`, {
        params: {
          ...(parentId && { parentId }),
          ...(permissionKeys && { permissionKeys }),
        },
      })
    );
  }

  listDistributorsWithPermissionKeys(dto: Distributor.ListDistributorsWithPermissionKeyQueryRequest) {
    return firstValueFrom(
      this.http.get<Distributor.ListDistributorsWithPermissionKeyQueryResponse>(`/api/distributors/permissionkeys`, {
        params: {
          ...(dto.page && { page: dto.page }),
          ...(dto.limit && { limit: dto.limit }),
          ...(dto.search && { search: dto.search }),
          ...(dto.permissionKeys && { permissionKeys: dto.permissionKeys }),
          permissionId: dto.permissionId,
        },
      })
    );
  }
}
